import { getLocale } from '@/hooks/helpers'
import { Tooltip } from '@/ui'
import { ChartBarIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import type { FormDropoffAnalyticsDataSchema, QuestionDropoffDataSchema } from '@jobilla/nexus-sdk'
import { motion } from 'motion/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type DateRangeOption = '7d' | '30d' | '90d' | 'all'

type QuestionDropoffAnalyticsProps = {
	data: FormDropoffAnalyticsDataSchema | null
	isLoading?: boolean
	onDateRangeChange?: (startDate: string, endDate: string) => void
}

export function QuestionDropoffAnalytics({
	data,
	isLoading,
	onDateRangeChange,
}: QuestionDropoffAnalyticsProps) {
	const { t } = useTranslation('analytics')
	const { t: tCommon } = useTranslation('common')
	const [selectedRange, setSelectedRange] = useState<DateRangeOption>('30d')

	const handleDateRangeChange = (range: DateRangeOption) => {
		setSelectedRange(range)
		if (onDateRangeChange) {
			const endDate = new Date().toISOString().split('T')[0]
			let startDate: string

			switch (range) {
				case '7d':
					startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
					break
				case '30d':
					startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
					break
				case '90d':
					startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
					break
				case 'all':
					startDate = '2020-01-01'
					break
			}

			onDateRangeChange(startDate, endDate)
		}
	}

	if (isLoading) {
		return (
			<div className="relative overflow-hidden p-6 rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 shadow-sm">
				<div className="relative">
					<div className="mb-4">
						<h2 className="text-xs uppercase tracking-wide font-semibold text-grey-500 mb-2">
							{t('dropoff.title')}
						</h2>
						<p className="text-sm text-grey-600">{t('dropoff.loading')}</p>
					</div>
					<div className="space-y-4">
						{[...Array(4)].map((_, i) => (
							<div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
						))}
					</div>
				</div>
			</div>
		)
	}

	if (!data || data.totalSessions === 0) {
		return (
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.2 }}
				className="relative overflow-hidden p-6 rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 shadow-sm"
			>
				<DropoffHeader
					selectedRange={selectedRange}
					onRangeChange={handleDateRangeChange}
					t={t}
					tCommon={tCommon}
				/>
				<div className="flex gap-4 items-start">
					<div className="text-slate-400 flex-shrink-0 p-2 bg-slate-100 rounded-lg">
						<ChartBarIcon className="size-6" />
					</div>
					<div>
						<h2 className="text-base font-semibold text-grey-900 mb-2">{t('dropoff.noDataTitle')}</h2>
						<p className="text-sm text-grey-600 leading-relaxed">
							{t('dropoff.noDataDescription')}
						</p>
					</div>
				</div>
			</motion.div>
		)
	}

	const questions = data.questions
	// Contact-consent wording is a legal requirement, so it must never be flagged for
	// optimisation based on dropoff. It stays visible in the list but is excluded from advice.
	const advisableQuestions = questions.filter(question => question.attribute !== 'contact_consent')
	const worstDropoff = advisableQuestions.length > 0
		? advisableQuestions.reduce(
			(worst, question) => (question.dropoffRate > worst.dropoffRate ? question : worst),
			advisableQuestions[0],
		)
		: null

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.2 }}
			className="relative overflow-hidden p-8 rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 shadow-sm"
		>
			<div className="relative">
				<DropoffHeader
					selectedRange={selectedRange}
					onRangeChange={handleDateRangeChange}
					t={t}
					tCommon={tCommon}
					subtitle={t('dropoff.subtitle')}
				/>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
					<SummaryCard
						label={t('dropoff.totalSessions')}
						value={formatNumber(data.totalSessions)}
						description={t('dropoff.totalSessionsDesc')}
						color="indigo"
					/>
					<SummaryCard
						label={t('dropoff.completionRate')}
						value={`${data.overallCompletionRate}%`}
						description={t(
							'dropoff.completed',
							{ count: formatNumber(data.completedSessions) } as Record<string, string>,
						)}
						color={data.overallCompletionRate >= 50
							? 'emerald'
							: data.overallCompletionRate >= 25
							? 'amber'
							: 'red'}
					/>
					{worstDropoff && worstDropoff.dropoffRate > 10 && (
						<SummaryCard
							label={t('dropoff.highestDropoff')}
							value={`${worstDropoff.dropoffRate}%`}
							description={`Q${worstDropoff.position + 1}: ${truncateLabel(worstDropoff.label, 30)}`}
							color="red"
						/>
					)}
				</div>

				<div className="space-y-3">
					<div className="text-xs uppercase tracking-wide font-semibold text-grey-500 mb-3">
						{t('dropoff.questionAnalysis')}
					</div>
					{questions.map((question, index) => (
						<QuestionRow
							key={question.questionId}
							question={question}
							index={index}
							totalSessions={data.totalSessions}
							t={t}
						/>
					))}
				</div>

				{worstDropoff && worstDropoff.dropoffRate > 25 && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.3, delay: 0.5 }}
						className="mt-8 p-5 bg-amber-50/70 border-2 border-amber-200 rounded-lg"
					>
						<h3 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
							<ExclamationTriangleIcon className="w-4 h-4" />
							{t('dropoff.optimizationSuggestion')}
						</h3>
						<p className="text-sm text-amber-800">
							{t('dropoff.optimizationText', {
								position: String(worstDropoff.position + 1),
								label: truncateLabel(worstDropoff.label, 50),
								rate: String(worstDropoff.dropoffRate),
							})}
						</p>
					</motion.div>
				)}
			</div>
		</motion.div>
	)
}

function SummaryCard({
	label,
	value,
	description,
	color,
}: {
	label: string
	value: string
	description: string
	color: 'indigo' | 'emerald' | 'amber' | 'red'
}) {
	const colorClasses = {
		indigo: {
			bg: 'bg-indigo-50/70',
			border: 'border-indigo-200',
			text: 'text-indigo-700',
			value: 'text-indigo-900',
		},
		emerald: {
			bg: 'bg-emerald-50/70',
			border: 'border-emerald-200',
			text: 'text-emerald-700',
			value: 'text-emerald-900',
		},
		amber: {
			bg: 'bg-amber-50/70',
			border: 'border-amber-200',
			text: 'text-amber-700',
			value: 'text-amber-900',
		},
		red: {
			bg: 'bg-red-50/70',
			border: 'border-red-200',
			text: 'text-red-700',
			value: 'text-red-900',
		},
	}

	const classes = colorClasses[color]

	return (
		<div className={`p-4 rounded-lg ${classes.bg} border-2 ${classes.border}`}>
			<div className={`text-xs font-semibold ${classes.text} uppercase tracking-wide mb-1`}>
				{label}
			</div>
			<div className={`text-2xl font-bold ${classes.value} mb-1`}>{value}</div>
			<div className="text-xs text-grey-600">{description}</div>
		</div>
	)
}

function QuestionRow({
	question,
	index,
	totalSessions,
	t,
}: {
	question: QuestionDropoffDataSchema
	index: number
	totalSessions: number
	t: (key: string, options?: Record<string, unknown>) => string
}) {
	const progressPercentage = totalSessions > 0
		? Math.round((question.sessionsAnswered / totalSessions) * 100)
		: 0

	const dropoffColor = question.dropoffRate < 10
		? 'bg-emerald-500'
		: question.dropoffRate < 25
		? 'bg-amber-500'
		: 'bg-red-500'

	const dropoffBadgeColor = question.dropoffRate < 10
		? 'bg-emerald-100 text-emerald-700'
		: question.dropoffRate < 25
		? 'bg-amber-100 text-amber-700'
		: 'bg-red-100 text-red-700'

	return (
		<motion.div
			initial={{ opacity: 0, x: -10 }}
			animate={{ opacity: 1, x: 0 }}
			transition={{ duration: 0.2, delay: index * 0.05 }}
			className="p-4 bg-white rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all"
		>
			<div className="flex items-start justify-between mb-3">
				<div className="flex items-start gap-3">
					<div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-semibold text-grey-700">
						{question.position + 1}
					</div>
					<div>
						<div className="text-sm font-medium text-grey-900 mb-1">
							{truncateLabel(question.label, 60)}
						</div>
						<div className="text-xs text-grey-500 capitalize">
							{t('dropoff.questionType', { type: question.type })}
						</div>
					</div>
				</div>
				<div className={`px-2 py-1 rounded-full text-xs font-semibold ${dropoffBadgeColor}`}>
					{t('dropoff.dropoffRate', { rate: String(question.dropoffRate) })}
				</div>
			</div>

			<div className="flex items-center gap-4">
				<div className="flex-1">
					<div className="h-2 bg-slate-100 rounded-full overflow-hidden">
						<div
							className={`h-full ${dropoffColor} transition-all duration-500`}
							style={{ width: `${100 - question.dropoffRate}%` }}
						/>
					</div>
				</div>
				<div className="flex gap-4 text-xs text-grey-600">
					<span>
						<span className="font-semibold text-grey-900">{formatNumber(question.sessionsViewed)}</span>
						{' '}
						{t('dropoff.viewed')}
					</span>
					<span>
						<span className="font-semibold text-grey-900">{formatNumber(question.sessionsAnswered)}</span>
						{' '}
						{t('dropoff.answered')}
					</span>
				</div>
			</div>
		</motion.div>
	)
}

function DropoffHeader({
	selectedRange,
	onRangeChange,
	t,
	tCommon,
	subtitle,
}: {
	selectedRange: DateRangeOption
	onRangeChange: (range: DateRangeOption) => void
	t: (key: string) => string
	tCommon: (key: string) => string
	subtitle?: string
}) {
	return (
		<div className="flex items-start justify-between mb-6 gap-4">
			<div>
				<div className={`flex items-center gap-2${subtitle ? ' mb-2' : ''}`}>
					<h2 className="text-xs uppercase tracking-wide font-semibold text-grey-500">
						{t('dropoff.title')}
					</h2>
					<Tooltip content={t('dropoff.betaTooltip')}>
						<span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-violet-100 text-violet-700 rounded cursor-help">
							{tCommon('common.beta')}
							<InformationCircleIcon className="w-3 h-3" />
						</span>
					</Tooltip>
				</div>
				{subtitle && <p className="text-sm text-grey-600">{subtitle}</p>}
			</div>
			<DateRangeSelector
				selectedRange={selectedRange}
				onRangeChange={onRangeChange}
				t={t}
			/>
		</div>
	)
}

function DateRangeSelector({
	selectedRange,
	onRangeChange,
	t,
}: {
	selectedRange: DateRangeOption
	onRangeChange: (range: DateRangeOption) => void
	t: (key: string) => string
}) {
	return (
		<div className="flex gap-1 bg-slate-100 rounded-lg p-1">
			{(['7d', '30d', '90d', 'all'] as DateRangeOption[]).map(range => (
				<button
					key={range}
					type="button"
					aria-pressed={selectedRange === range}
					onClick={() => onRangeChange(range)}
					className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
						selectedRange === range
							? 'bg-white text-grey-900 shadow-sm'
							: 'text-grey-600 hover:text-grey-900'
					}`}
				>
					{t(`dropoff.dateRange.${range}`)}
				</button>
			))}
		</div>
	)
}

function formatNumber(num: number): string {
	return new Intl.NumberFormat(getLocale()).format(num)
}

function truncateLabel(label: string, maxLength: number): string {
	if (label.length <= maxLength) { return label }
	return label.slice(0, maxLength - 3) + '...'
}
